import { useCallback, useState } from 'react';
import pdfToCard from 'lottie/pdf-to-card.json';
import cardToPdf from 'lottie/card-to-pdf.json';

import { LayoutContainer } from 'layout';
import { Dots, DotsScrollCard, InvertedBackground } from 'ui';
import { colorBgInvertedPrimaryTwo } from '../../../ui/invertedBackground/inverted-background.module.scss';

import { Card } from './card';
import {
    activeTab,
    cardContainer,
    sectionContainer,
    layoutContainer,
    tabsWrapper,
    knowledge
} from './tabs-section.module.scss';

export const cards: DotsScrollCard[] = [
    {
        name: 'Collect & Connect',
        title: 'Cards take you beyond PDFs and websites.',
        description:
            'Capture new knowledge from PDFs and websites. Connect it to related content using your Elumity cards. Bring together the information that belongs together. Push the PDF or source to the background where it belongs.',
        animation: pdfToCard,
        index: 0
    },
    {
        name: 'PDF Links',
        title: 'Cards have PDFs in the background',
        description:
            'Stop searching for PDFs that provide only the first step in your work. Find cards that contain the information you need while keeping source PDFs background-linked and readily available.',
        animation: cardToPdf,
        index: 1
    }
];

export const TabsSection = () => {
    const [activeIndex, setActiveIndex] = useState(0); // activeIndex: next card to be displayed
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [touchStartX, setTouchStartX] = useState(0);

    const handleCardSwitch = useCallback(
        (index: number) => {
            if (index !== activeIndex && !isTransitioning) {
                setIsTransitioning(true);

                setTimeout(() => {
                    setActiveIndex(index);
                    setIsTransitioning(false);
                }, 300); // Adjust the timing here as needed (in milliseconds)
            }
        },
        [activeIndex, isTransitioning]
    );

    const handleAnimationEnding = (index: number) => {
        index < cards.length - 1 ? handleCardSwitch(index + 1) : handleCardSwitch(0);
    };

    const handleSwipeStart = (e: React.TouchEvent<HTMLDivElement>) => {
        setTouchStartX(e.touches[0].clientX);
    };

    const handleSwipeEnd = (e: React.TouchEvent<HTMLDivElement>) => {
        const touchEndX = e.changedTouches[0].clientX;
        const swipeDistance = touchEndX - touchStartX;
        const threshold = 40;

        if (swipeDistance > threshold) {
            handleCardSwitch((activeIndex + cards.length - 1) % cards.length);
        } else if (swipeDistance < -threshold) {
            handleCardSwitch((activeIndex + 1) % cards.length);
        }
    };

    return (
        <InvertedBackground style={{ backgroundColor: colorBgInvertedPrimaryTwo }}>
            <LayoutContainer className={layoutContainer}>
                <section className={sectionContainer}>
                    <h3>Knowledge cards as your second brain</h3>
                    <p>Collect information in context, on cards. Bring together what belongs together.</p>
                    <div className={tabsWrapper}>
                        {cards.map((card, index) => (
                            <button
                                key={card.name}
                                id={card.name}
                                onClick={() => handleCardSwitch(index)}
                                disabled={isTransitioning}>
                                <h5 className={activeIndex === index ? activeTab : ''}>{card.name}</h5>
                            </button>
                        ))}
                    </div>
                    <div className={cardContainer} onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd}>
                        {/* slight fading between card changes (image) */}
                        <Card
                            title={cards[activeIndex].title}
                            description={cards[activeIndex].description}
                            animation={cards[activeIndex].animation}
                            isVisible={!isTransitioning}
                            index={cards[activeIndex].index}
                            className={knowledge}
                            handleAnimationEnding={index => handleAnimationEnding(index)}
                        />
                        <Dots
                            cards={cards}
                            activeIndex={activeIndex}
                            handleDotClick={index => handleCardSwitch(index)}
                        />
                    </div>
                </section>
            </LayoutContainer>
        </InvertedBackground>
    );
};
